import { Point } from "pixi.js";
import type { IFloorPayload } from "../Domain/Types/FloorPlan/FloorPayload.type";
import type { IPlaceSchedule } from "../Domain/Types/FloorPlan/PlaceSchedule";
import type { IViewport } from "../Domain/Types/FloorPlan/Viewport.type";
import type { IZoneSchedule } from "../Domain/Types/FloorPlan/ZoneSchedule";

/** How zone/place coordinates relate to the outline image vs stored viewport metadata. */
export type FloorPlanCoordinateSpace = "viewport" | "image";

type FloorPlanGeometrySource = Pick<IFloorPayload, "zones" | "places" | "viewport">;
type ImageDimensions = Pick<IViewport, "width" | "height">;

const IMAGE_BOUND_TOLERANCE = 1.02;
const VIEWPORT_IMAGE_SIZE_TOLERANCE = 0.1;
/**
 * When geometry fits both rectangles, prefer image space only if it fills the image
 * clearly more than the viewport (avoids sparse top-left false positives).
 */
const IMAGE_USAGE_PREFERENCE_RATIO = 1.5;
/**
 * Absolute image fill above which a modestly oversized viewport is treated as stale
 * metadata for image-space geometry (KSB Frankenthal / Pfalz-style floors).
 */
const HIGH_IMAGE_FILL_THRESHOLD = 0.85;
/** Sparse floors stay on viewport space when both rectangles fit — unless high-fill override. */
const LEGACY_GEOMETRY_COUNT_THRESHOLD = 5;

export type FloorPlanGeometryBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export function computeFloorPlanGeometryBounds(
  zones: IZoneSchedule[],
  places: IPlaceSchedule[]
): FloorPlanGeometryBounds | undefined {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const zone of zones) {
    for (const coordinate of zone.coordinates) {
      minX = Math.min(minX, coordinate.x);
      minY = Math.min(minY, coordinate.y);
      maxX = Math.max(maxX, coordinate.x);
      maxY = Math.max(maxY, coordinate.y);
    }
  }

  for (const place of places) {
    minX = Math.min(minX, place.position.x);
    minY = Math.min(minY, place.position.y);
    maxX = Math.max(maxX, place.position.x);
    maxY = Math.max(maxY, place.position.y);
  }

  if (!Number.isFinite(minX)) {
    return undefined;
  }

  return { minX, minY, maxX, maxY };
}

function fitsBounds(
  bounds: FloorPlanGeometryBounds,
  width: number,
  height: number,
  tolerance: number
): boolean {
  return bounds.maxX <= width * tolerance && bounds.maxY <= height * tolerance;
}

function spaceUsage(bounds: FloorPlanGeometryBounds, width: number, height: number): number {
  return Math.max(bounds.maxX / width, bounds.maxY / height);
}

/**
 * Prefer image space when viewport metadata is larger than the outline on both axes
 * and geometry fills the image tightly (stale oversized viewport, image-authored coords).
 */
function shouldPreferImageForOversizedViewport(
  bounds: FloorPlanGeometryBounds,
  image: ImageDimensions,
  viewport: ImageDimensions
): boolean {
  if (image.width >= viewport.width || image.height >= viewport.height) {
    return false;
  }

  if (!fitsBounds(bounds, image.width, image.height, IMAGE_BOUND_TOLERANCE)) {
    return false;
  }

  const imageUsage = spaceUsage(bounds, image.width, image.height);
  const viewportUsage = spaceUsage(bounds, viewport.width, viewport.height);

  return imageUsage >= HIGH_IMAGE_FILL_THRESHOLD && imageUsage > viewportUsage;
}

/**
 * Picks whether zone/place coordinates were authored against the outline image
 * or the stored viewport rectangle.
 *
 * Decision order:
 * 1. Missing sizes → viewport
 * 2. Viewport ≈ image → viewport, unless oversized + high image fill → image
 * 3. Geometry fits only one of image/viewport → that space
 * 4. Fits neither → viewport
 * 5. Fits both, sparse geometry → viewport, unless oversized + high image fill → image
 * 6. Fits both, image clearly more filled than viewport → image
 * 7. Fits both, oversized viewport + high image fill → image
 * 8. Otherwise → viewport
 */
export function resolveFloorPlanCoordinateSpace(
  floorPlan: FloorPlanGeometrySource,
  background: ImageDimensions
): FloorPlanCoordinateSpace {
  const { viewport, zones, places } = floorPlan;
  const imageWidth = background.width;
  const imageHeight = background.height;
  const viewportWidth = viewport.width;
  const viewportHeight = viewport.height;

  if (!imageWidth || !imageHeight || !viewportWidth || !viewportHeight) {
    return "viewport";
  }

  const viewportMatchesImage =
    Math.abs(viewportWidth - imageWidth) / viewportWidth < VIEWPORT_IMAGE_SIZE_TOLERANCE &&
    Math.abs(viewportHeight - imageHeight) / viewportHeight < VIEWPORT_IMAGE_SIZE_TOLERANCE;

  const bounds = computeFloorPlanGeometryBounds(zones, places);
  if (!bounds) {
    return "viewport";
  }

  if (viewportMatchesImage) {
    if (shouldPreferImageForOversizedViewport(bounds, background, viewport)) {
      return "image";
    }

    return "viewport";
  }

  const fitsImage = fitsBounds(bounds, imageWidth, imageHeight, IMAGE_BOUND_TOLERANCE);
  const fitsViewport = fitsBounds(bounds, viewportWidth, viewportHeight, IMAGE_BOUND_TOLERANCE);

  if (fitsImage && !fitsViewport) {
    return "image";
  }

  if (fitsViewport && !fitsImage) {
    return "viewport";
  }

  if (!fitsImage && !fitsViewport) {
    return "viewport";
  }

  const geometryCount = zones.length + places.length;
  if (geometryCount < LEGACY_GEOMETRY_COUNT_THRESHOLD) {
    if (shouldPreferImageForOversizedViewport(bounds, background, viewport)) {
      return "image";
    }

    return "viewport";
  }

  const imageUsage = spaceUsage(bounds, imageWidth, imageHeight);
  const viewportUsage = spaceUsage(bounds, viewportWidth, viewportHeight);

  if (imageUsage >= viewportUsage * IMAGE_USAGE_PREFERENCE_RATIO) {
    return "image";
  }

  if (shouldPreferImageForOversizedViewport(bounds, background, viewport)) {
    return "image";
  }

  return "viewport";
}

export function getFloorPlanCanvasSize(
  viewport: IViewport,
  background: ImageDimensions,
  coordinateSpace: FloorPlanCoordinateSpace
): IViewport {
  if (coordinateSpace === "image") {
    return { width: background.width, height: background.height };
  }

  return { width: viewport.width, height: viewport.height };
}

export function resolveFloorPlanCanvasSize(
  floorPlan: FloorPlanGeometrySource,
  background: ImageDimensions
): IViewport {
  const coordinateSpace = resolveFloorPlanCoordinateSpace(floorPlan, background);
  return getFloorPlanCanvasSize(floorPlan.viewport, background, coordinateSpace);
}

export function getFloorPlanCanvasCorner(canvasSize: IViewport): Point {
  return new Point(canvasSize.width, canvasSize.height);
}

/**
 * Desk/icon scale relative to stored bounding boxes.
 * Always uses the historical width ratio so Create and Select match for both
 * image-space and viewport-space floors (bounding boxes are not native pixels).
 */
export function getFloorPlanPlaceScale(background: ImageDimensions, viewport: IViewport): number {
  if (!background.width || !viewport.width) {
    return 0;
  }

  return background.width / viewport.width;
}

export function resolveFloorPlanPlaceScale(
  floorPlan: FloorPlanGeometrySource,
  background: ImageDimensions
): number {
  return getFloorPlanPlaceScale(background, floorPlan.viewport);
}

/**
 * Viewport for a newly uploaded / created outline: match natural image pixels
 * so Create starts on the healthy identity path (no stretch heuristics).
 */
export function getIdentityViewportFromImage(image: ImageDimensions): IViewport {
  return { width: image.width, height: image.height };
}
